import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePenpal } from '../hooks/usePenpal';
import { useUserStore } from '../store/userStore';
import { apiClient } from '../services/api';
import LoadingOverlay from './layout/LoadingOverlay';
import * as Sentry from '@sentry/react';

interface AuthGuardProps {
  children: React.ReactNode;
}

const CLIENT_APP_INFO = {
  appVersion: 'standalone',
  platform: 'web',
  arch: 'web'
} as const;

/**
 * Component that handles authentication for standalone mode
 * In iframe mode, authentication is handled by Penpal
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isInIframe: inIframe, isConnected } = usePenpal();
  const { token, login, logout } = useUserStore();
  const hasInitialized = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only run once
    if (hasInitialized.current) {
      return;
    }

    // Skip if already on login page
    if (location.pathname === '/login') {
      setIsInitialized(true);
      hasInitialized.current = true;
      return;
    }

    // Handle iframe mode - auth is handled by Penpal
    if (inIframe) {
      if (!isConnected) {
        // Give Penpal time to connect
        setTimeout(() => {
          setIsInitialized(true);
          hasInitialized.current = true;
        }, 2000);
        return;
      }
      setIsInitialized(true);
      hasInitialized.current = true;
      return;
    }

    // For standalone mode: load UI immediately, refresh data in background
    const currentState = useUserStore.getState();
    const storedToken = currentState.token || localStorage.getItem('folge-session-token');
    const storedAccount = currentState.account || (() => {
      try {
        const stored = localStorage.getItem('folge-user-account');
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    })();

    if (storedToken && storedAccount) {
      // Initialize API client immediately with cached token
      apiClient.createApi(storedToken, true);
      
      // Restore account from store if needed
      if (!currentState.account) {
        const storedProperties = (() => {
          try {
            const stored = localStorage.getItem('folge-user-properties');
            return stored ? JSON.parse(stored) : null;
          } catch {
            return null;
          }
        })();
        
        useUserStore.setState({ 
          token: storedToken, 
          account: storedAccount,
          properties: storedProperties
        });
      }
      
      // Load UI immediately
      setIsInitialized(true);
      hasInitialized.current = true;
      console.log('AuthGuard initialized');
      // Refresh user data in background after 1 second (only once)
      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          const sessionResponse = await apiClient.checkSession();
          
          if (sessionResponse.ok && sessionResponse.me && sessionResponse.properties) {
            const sessionToken = sessionResponse.token || storedToken;
            
            // Update API client with token
            apiClient.createApi(sessionToken, true);
            
            // Update user store with fresh data
            login(
              sessionToken,
              sessionResponse.me,
              sessionResponse.properties,
              CLIENT_APP_INFO
            );
          } else {
            // Session invalid - logout
            logout();
            const currentPath = window.location.pathname;
            if (currentPath !== '/login') {
              navigate('/login', { replace: true });
            }
          }
        } catch (error) {
          console.error('Background auth refresh failed:', error);
          Sentry.captureException(error);
          // Don't logout on error - user can still use cached data
        }
      }, 1000);
    } else {
      // No token/account - redirect to login
      logout();
      navigate('/login', { replace: true });
      setIsInitialized(true);
      hasInitialized.current = true;
    }

    // Cleanup timeout on unmount
    return () => {
    };
  }, [location.pathname, inIframe, isConnected, login, logout, navigate]);

  if (!isInitialized) {
    return <LoadingOverlay message="Loading..." />;
  }

  // If not authenticated and not on login page, show nothing (redirect is happening)
  if (!token && !inIframe && location.pathname !== '/login') {
    return null;
  }

  return <>{children}</>;
};

